import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { authService } from './services/authService';
import { AdminUser } from './types';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import UserManagementPage from './pages/UserManagementPage';
import YouTubePlaylistPage from './pages/YouTubePlaylistPage';
import ContentManagementPage from './pages/ContentManagementPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SystemHealthPage from './pages/SystemHealthPage';

// ============================================================================
// MAIN APP COMPONENT
// ============================================================================

const App: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    // Restore authentication state on app load
    const initializeAuth = async () => {
      try {
        const user = await authService.restoreAuthState();
        setCurrentUser(user);
      } catch (error) {
        console.error('Failed to restore auth state:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Show loading screen while checking authentication
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          currentUser ? (
            <Navigate to="/dashboard/users" replace />
          ) : (
            <LoginPage onLoginSuccess={setCurrentUser} />
          )
        }
      />

      {/* Protected routes */}
      <Route
        path="/dashboard/*"
        element={
          currentUser ? (
            <DashboardLayout currentUser={currentUser} onLogout={() => setCurrentUser(null)}>
              <Routes>
                <Route path="users" element={<UserManagementPage />} />
                <Route path="playlists" element={<YouTubePlaylistPage />} />
                <Route path="content" element={<ContentManagementPage />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="health" element={<SystemHealthPage />} />
                <Route path="" element={<Navigate to="users" replace />} />
              </Routes>
            </DashboardLayout>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Default redirect */}
      <Route
        path="*"
        element={
          <Navigate to={currentUser ? "/dashboard/users" : "/login"} replace />
        }
      />
    </Routes>
  );
};

export default App; 